import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plane, ShieldCheck, Loader2, Wallet, CreditCard, Smartphone, Landmark,
  Wallet2, BadgeCheck, Lock, ArrowLeft, PlaneTakeoff, PlaneLanding,
  Armchair, Luggage, Utensils, User, AlertTriangle, CheckCircle2, Check,
} from "lucide-react";
import Header, { TopBar } from "@/components/Header";
import Footer from "@/components/Footer";
import SeatMap from "@/components/SeatMap";
import { airlineLogo, formatINR } from "@/mock";
import {
  apiReviewFlight, apiFlightSeatmap, apiBookFlightLive, apiCreateBooking,
  apiSupplierReadiness, apiGuestCheckout, apiOtpVerify, apiAutoRefund,
} from "@/api";
import { useAuth } from "@/context/AuthContext";
import { TravellerSuggest, ContactDatalists, useSavedContacts, refreshSavedTravellers } from "@/components/TravellerSuggest";
import { payWithRazorpay } from "@/lib/razorpay";
import { downloadReceiptPdf } from "@/lib/invoicePdf";
import { toast } from "sonner";

const PAY_METHODS = [
  { id: "upi", label: "UPI", sub: "GPay / PhonePe / Paytm", icon: Smartphone },
  { id: "card", label: "Card", sub: "Credit / Debit", icon: CreditCard },
  { id: "netbanking", label: "Net Banking", sub: "All major banks", icon: Landmark },
  { id: "wallet", label: "Wallets", sub: "Paytm / Mobikwik", icon: Wallet2 },
];
const TITLES = ["Mr", "Mrs", "Ms", "Master", "Miss"];

const STEPS = [
  { id: 1, label: "Review Itinerary" },
  { id: 2, label: "Travellers" },
  { id: 3, label: "Seats & Add-ons" },
  { id: 4, label: "Payment" },
];

const Checkout = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [step, setStep] = useState(1);

  const [review, setReview] = useState(null);
  const [reviewRet, setReviewRet] = useState(null);
  const [seatMaps, setSeatMaps] = useState([]);
  const [seatsNA, setSeatsNA] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(true);
  const [reviewError, setReviewError] = useState("");

  const [travellers, setTravellers] = useState([]);
  const [activePax, setActivePax] = useState(0);
  const [contact, setContact] = useState({ email: "", phone: "" });
  const savedContacts = useSavedContacts();
  const isAgent = user?.role === "agent";
  useEffect(() => {
    if (user && !isAgent) setContact((c) => (c.email || c.phone ? c : { email: user.email || "", phone: user.phone || "" }));
  }, [user, isAgent]);
  const pickTraveller = (i, t) => {
    setPax(i, { ti: t.title || "Mr", fN: t.firstName, lN: t.lastName, dob: t.dob || "", pNum: t.passport || "",
      pNat: t.nationality || "IN", pep: t.passportExpiry || "", pid: t.passportIssue || "", pan: t.pan || "" });
    if (i === 0 && isAgent && t.email) setContact({ email: t.email, phone: t.phone || "" });
  };
  const onContactEmail = (email) => {
    const hit = isAgent && savedContacts.find((c) => c.email === email);
    setContact((c) => ({ ...c, email, phone: hit && hit.phone ? hit.phone : c.phone }));
  };
  const [method, setMethod] = useState("upi");
  const [paying, setPaying] = useState(false);
  const [result, setResult] = useState(null);
  const [supplierBlock, setSupplierBlock] = useState(null);
  const [otpFlow, setOtpFlow] = useState(null);
  const [otpCode, setOtpCode] = useState("");
  const { setSession } = useAuth();

  useEffect(() => {
    if (step === 4 && user?.role !== "agent") {
      apiSupplierReadiness("flight")
        .then((r) => { if (!r.ready) setSupplierBlock(r.reason || "Booking temporarily unavailable"); })
        .catch(() => {});
    }
  }, [step, user]);

  useEffect(() => {
    let saved = null;
    try { saved = JSON.parse(sessionStorage.getItem("ft_checkout") || "null"); } catch { saved = null; }
    setData(saved);
    const legs = saved?.roundtrip ? (saved.legs || []) : (saved?.flight ? [saved.flight] : []);
    if (!legs.length) { setReviewLoading(false); return; }

    const a = saved.adults || saved.pax || 1;
    const c = saved.children || 0;
    const inf = saved.infants || 0;
    const slots = [];
    const base = { ti: "Mr", fN: "", lN: "", dob: "", pNum: "", pNat: "IN", pep: "", pid: "", pan: "", seat: {}, baggage: "", meal: "" };
    for (let i = 0; i < a; i++) slots.push({ ...base, pt: "ADULT" });
    for (let i = 0; i < c; i++) slots.push({ ...base, ti: "Master", pt: "CHILD" });
    for (let i = 0; i < inf; i++) slots.push({ ...base, ti: "Master", pt: "INFANT" });
    setTravellers(slots);

    const priceIds = saved.comboPriceId
      ? [saved.comboPriceId]
      : legs.map((l) => l.priceId).filter(Boolean);
    if (!saved.comboPriceId && priceIds.length !== legs.length) { setReviewLoading(false); return; }
    setReviewLoading(true);
    // Round-trip: TripJack requires BOTH priceIds in ONE combined review call;
    // the returned bookingId covers both legs.
    apiReviewFlight(priceIds)
      .then(async (rev) => {
        if (rev.source !== "tripjack") {
          setReviewError(rev.error || "Fare no longer available. Please search again.");
          return;
        }
        setReview(rev);
        if (rev.segmentsRet?.length) setReviewRet({ segments: rev.segmentsRet });
        if (rev.bookingId) {
          const sm = await apiFlightSeatmap(rev.bookingId).catch(() => null);
          if (sm?.seatMaps) setSeatMaps(sm.seatMaps);
          if (sm?.notApplicable) setSeatsNA(true);
        }
      })
      .catch(() => setReviewError("Could not validate fare. Please retry."))
      .finally(() => setReviewLoading(false));
  }, []);

  const isRT = !!data?.roundtrip;

  const walletBalance = user?.wallet || 0;
  const liveMode = !!review;
  const cond = review?.conditions || {};
  const segs = review?.segments || [];

  const addonTotal = useMemo(() => {
    let sum = 0;
    travellers.forEach((t) => {
      Object.values(t.seat || {}).forEach((s) => (sum += s?.amount || 0));
      if (t.baggage) sum += review?.baggage?.find((b) => b.code === t.baggage)?.amount || 0;
      if (t.meal) sum += review?.meals?.find((m) => m.code === t.meal)?.amount || 0;
    });
    return sum;
  }, [travellers, review]);

  const baseTotal = useMemo(() => {
    if (liveMode) return review.fare.total || 0;   // combined fare for round-trip (single review)
    return (data?.flight?.price || 0) * (data?.pax || 1);
  }, [liveMode, review, data]);

  const grandTotal = Math.round(baseTotal + addonTotal);

  const setPax = (i, patch) =>
    setTravellers((prev) => prev.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));

  const assignSeat = (segId, seat) =>
    setTravellers((prev) =>
      prev.map((t, idx) => {
        const cleaned = { ...(t.seat || {}) };
        if (cleaned[segId]?.seatNo === seat.seatNo && idx === activePax) {
          delete cleaned[segId];
          return { ...t, seat: cleaned };
        }
        if (idx === activePax) return { ...t, seat: { ...cleaned, [segId]: seat } };
        if (cleaned[segId]?.seatNo === seat.seatNo) { delete cleaned[segId]; return { ...t, seat: cleaned }; }
        return t;
      })
    );

  const validateTravellers = () => {
    for (const t of travellers) {
      if (!t.fN.trim() || !t.lN.trim()) { toast.error("Please enter every traveller's first & last name"); return false; }
      if (cond.dobRequired && !t.dob) { toast.error("Date of birth required for all travellers"); return false; }
      if (cond.passportRequired && !t.pNum.trim()) { toast.error("Passport number required for this international flight"); return false; }
      if ((cond.passportRequired || cond.passportExpiry) && !t.pep) { toast.error("Passport expiry date required for this flight"); return false; }
      if ((cond.passportRequired || cond.passportIssue) && !t.pid) { toast.error("Passport issue date required for this flight"); return false; }
      if (cond.panRequired && !t.pan.trim()) { toast.error("PAN number required for this flight"); return false; }
    }
    if (!contact.email.trim() || !contact.phone.trim()) { toast.error("Enter contact email & mobile"); return false; }
    return true;
  };

  const doBook = async (paymentId = null) => {
    setPaying(true);
    try {
      if (liveMode) {
        // Combined review => one bookingId covers all legs (incl. round-trip return)
        const payloadTravellers = travellers.map((t) => {
          const firstSeat = Object.values(t.seat || {})[0];
          return {
            ti: t.ti, fN: t.fN.trim(), lN: t.lN.trim(), pt: t.pt,
            dob: t.dob || null,
            pNum: t.pNum || null,
            pNat: (cond.passportRequired || cond.passportExpiry) ? (t.pNat || "IN") : null,
            pep: t.pep || null,
            pid: t.pid || null,
            pan: t.pan || null,
            ssrSeat: firstSeat?.code || null,
            ssrBaggage: t.baggage || null,
            ssrMeal: t.meal || null,
          };
        });
        const res = await apiBookFlightLive({
          bookingId: review.bookingId,
          amount: baseTotal,
          travellers: payloadTravellers,
          email: contact.email.trim(),
          phone: contact.phone.trim(),
          fromCode: data.fromCode, toCode: data.toCode,
          fromCity: data.fromCity, toCity: data.toCity,
          date: data.date,
          airline: data.flight?.airline || data.legs?.[0]?.airline || "",
          flightNo: data.flight?.flightNo || data.legs?.[0]?.flightNo || "",
          paymentMethod: isAgent ? "wallet" : "gateway",
          razorpayPaymentId: paymentId,
        });
        if (res.success) { refreshSavedTravellers(); setResult({ success: true, pnr: res.bookingId }); }
        else {
          let refunded = false;
          if (paymentId) {
            try { await apiAutoRefund({ paymentId, reason: res.error || "Booking failed at supplier" }); refunded = true; }
            catch { /* support handles manually */ }
          }
          setResult({ success: false, error: res.error || "Booking failed at TripJack", errCode: res.errCode, refunded });
        }
      } else {
        const res = await apiCreateBooking({
          flight: data.flight, fromCode: data.fromCode, toCode: data.toCode,
          fromCity: data.fromCity, toCity: data.toCity, date: data.date,
          cabin: data.cabin, pax: data.pax,
          passenger: { name: `${travellers[0].fN} ${travellers[0].lN}`, email: contact.email, phone: contact.phone },
          unitPrice: data.flight.price,
          razorpayPaymentId: paymentId,
        });
        refreshSavedTravellers(); setResult({ success: true, pnr: res.booking.pnr, demo: true, booking: res.booking });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Payment failed. Please try again.";
      if (err?.response?.status === 401) { toast.error("Session expired. Please login again."); navigate("/login"); }
      else setResult({ success: false, error: detail });
    } finally {
      setPaying(false);
    }
  };

  const payNow = async (e) => {
    e.preventDefault();
    if (!user) {
      // MMT-style guest checkout: auto-create account from entered details,
      // or OTP to email if this email already has an account.
      if (!contact.email.trim() || !contact.phone.trim()) {
        toast.error("Fill traveller & contact details first (step 2) so we can create your booking account");
        setStep(2);
        return;
      }
      setPaying(true);
      try {
        const g = await apiGuestCheckout({
          name: `${travellers[0]?.fN || ""} ${travellers[0]?.lN || ""}`.trim() || contact.email.split("@")[0],
          email: contact.email.trim(), phone: contact.phone.trim(),
        });
        if (g.otp_required) {
          setOtpFlow({ email: contact.email.trim() });
          setOtpCode("");
          setPaying(false);
          toast.info("This email has bookings already — we sent an OTP to your email");
          return;
        }
        setSession(g.token, g.user);
        toast.success("Account created! Your ticket will be saved to your email.");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Could not start guest checkout");
        setPaying(false);
        return;
      }
    }
    if (isAgent) {
      if (walletBalance < grandTotal) { toast.error(`Insufficient wallet balance. Need ${formatINR(grandTotal)}.`); return; }
      doBook();
      return;
    }
    // Customer -> Razorpay (with supplier readiness pre-check)
    setPaying(true);
    try {
      const rd = await apiSupplierReadiness("flight");
      if (!rd.ready) {
        setSupplierBlock(rd.reason || "Booking temporarily unavailable");
        toast.error(rd.reason || "Booking temporarily unavailable");
        setPaying(false);
        return;
      }
    } catch { /* readiness service hiccup - continue, server still guards */ }
    const pay = await payWithRazorpay({
      amount: grandTotal, purpose: "flight", contact,
      description: `Flight ${data?.fromCode || ""}-${data?.toCode || ""}`,
    });
    setPaying(false);
    if (pay?.paymentId) doBook(pay.paymentId);
  };

  const verifyOtp = async () => {
    if (otpCode.trim().length !== 6) { toast.error("Enter the 6-digit OTP from your email"); return; }
    setPaying(true);
    try {
      const res = await apiOtpVerify({ email: otpFlow.email, otp: otpCode.trim() });
      setSession(res.token, res.user);
      setOtpFlow(null);
      toast.success("Verified! Your booking continues — all trips stay saved on your email.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "OTP verification failed");
    } finally {
      setPaying(false);
    }
  };

  // ---------------- guards ----------------
  if (loading || reviewLoading) {
    return (
      <div className="min-h-screen bg-[#F5F7FB]">
        <TopBar /><Header />
        <div className="mx-auto max-w-md px-4 py-28 text-center">
          <Loader2 className="mx-auto h-9 w-9 animate-spin text-[#1B1F5B]" />
          <p className="mt-3 text-sm font-semibold text-slate-500">Validating live fare & seat map…</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!data || !data.flight) {
    return (
      <div className="min-h-screen bg-[#F5F7FB]">
        <TopBar /><Header />
        <div className="mx-auto max-w-xl px-4 py-24 text-center">
          <Plane className="mx-auto h-10 w-10 text-slate-300" />
          <h1 className="mt-3 text-xl font-extrabold text-slate-700">No flight selected</h1>
          <button onClick={() => navigate("/")} className="mt-5 rounded-lg bg-[#1B1F5B] px-6 py-2.5 text-sm font-bold text-white hover:bg-[#141844]">Back to Search</button>
        </div>
        <Footer />
      </div>
    );
  }

  if (reviewError) {
    return (
      <div className="min-h-screen bg-[#F5F7FB]">
        <TopBar /><Header />
        <div className="mx-auto max-w-xl px-4 py-24 text-center">
          <AlertTriangle className="mx-auto h-10 w-10 text-amber-400" />
          <h1 className="mt-3 text-xl font-extrabold text-slate-700">Fare expired</h1>
          <p className="mt-1 text-sm text-slate-400">{reviewError}</p>
          <button onClick={() => navigate(-1)} className="mt-5 rounded-lg bg-[#1B1F5B] px-6 py-2.5 text-sm font-bold text-white hover:bg-[#141844]">Back to results</button>
        </div>
        <Footer />
      </div>
    );
  }

  // ---------------- RESULT ----------------
  if (result) {
    return (
      <div className="min-h-screen bg-[#F5F7FB]">
        <TopBar /><Header />
        <div className="mx-auto max-w-xl px-4 py-16">
          <div className="rounded-3xl border border-slate-100 bg-white p-8 text-center shadow-[0_20px_60px_rgba(27,31,91,0.14)]">
            {result.success ? (
              <>
                <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50"><BadgeCheck className="h-9 w-9 text-emerald-500" /></span>
                <h1 className="mt-4 font-[Poppins] text-2xl font-extrabold text-slate-800">Booking Confirmed!</h1>
                <p className="mt-1 text-sm text-slate-500">Your ticket is booked. Confirmation sent to {contact.email}.</p>
                <div className="mt-6 rounded-2xl bg-[#F5F7FB] p-5">
                  <p className="text-xs uppercase tracking-widest text-slate-400">PNR / Booking ID</p>
                  <p className="mt-1 font-mono text-3xl font-extrabold tracking-wider text-[#1B1F5B]">{result.pnr}</p>
                </div>
                {result.booking && (
                  <button data-testid="flight-receipt-btn" onClick={() => downloadReceiptPdf(result.booking, { total: result.booking.total })}
                    className="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-4 py-2 text-xs font-bold text-slate-600 hover:border-[#1B1F5B]">
                    Download Receipt
                  </button>
                )}
                {result.partial && (
                  <div data-testid="partial-refund-note" className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-left text-[12px] leading-relaxed text-amber-800">
                    {result.partial} Onward ticket confirm hai (PNR upar). Return leg ke liye dobara try karein.
                  </div>
                )}
              </>
            ) : (
              <>
                <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-50"><AlertTriangle className="h-9 w-9 text-amber-500" /></span>
                <h1 className="mt-4 font-[Poppins] text-2xl font-extrabold text-slate-800">Booking could not be issued</h1>
                <p className="mx-auto mt-2 max-w-sm text-sm text-slate-500">
                  TripJack response: <span className="font-bold text-red-600">{result.error}</span>
                </p>
                {result.refunded && (
                  <div data-testid="auto-refund-note" className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-left text-[12px] leading-relaxed text-emerald-800">
                    ✅ <b>Aapka payment auto-refund ho gaya hai.</b> Ticket issue nahi ho payi isliye poora paisa Razorpay se wapas ja raha hai (5-7 working days mein account mein aa jayega). Koi paisa fasa nahi hai.
                  </div>
                )}
                {result.partial && (
                  <div data-testid="partial-refund-note" className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-left text-[12px] leading-relaxed text-amber-800">
                    {result.partial} Onward ticket confirm hai (PNR upar). Return leg ke liye dobara try karein.
                  </div>
                )}
                {/insufficient/i.test(result.error || "") && (
                  <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-left text-[12px] leading-relaxed text-amber-800">
                    Poora flow live TripJack tak pahunch gaya ✅ — search, fare review, seat map, add-ons aur book request sab live chali. Ab sirf TripJack wallet me balance add karte hi asli ticket issue hone lagega.
                  </div>
                )}
              </>
            )}
            <div className="mt-6 flex gap-3">
              <button onClick={() => navigate("/dashboard")} className="flex-1 rounded-xl bg-[#1B1F5B] py-3 text-sm font-bold text-white hover:bg-[#141844]">My Bookings</button>
              <button onClick={() => navigate("/")} className="flex-1 rounded-xl border border-slate-200 py-3 text-sm font-bold text-slate-600 hover:bg-slate-50">Book Another</button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // ---------------- MAIN ----------------
  const f = data.flight;
  const hasAddons = liveMode && (review.baggage.length > 0 || review.meals.length > 0);
  const hasSeats = liveMode && seatMaps.length > 0;

  const nextStep = () => {
    if (step === 2 && !validateTravellers()) return;
    setStep((s) => Math.min(4, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#F5F7FB]">
      <TopBar /><Header />

      {/* Stepper */}
      <div className="border-b border-slate-100 bg-white">
        <div className="mx-auto flex max-w-5xl items-center gap-0 px-4 py-4">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.id}>
              <button type="button" onClick={() => s.id < step && setStep(s.id)} data-testid={`step-${s.id}`}
                className="flex items-center gap-2 disabled:cursor-default" disabled={s.id > step}>
                <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-extrabold transition-colors ${
                  s.id < step ? "bg-emerald-500 text-white" : s.id === step ? "bg-[#1B1F5B] text-white" : "bg-slate-200 text-slate-500"}`}>
                  {s.id < step ? <Check className="h-4 w-4" /> : s.id}
                </span>
                <span className={`text-[13px] font-bold ${s.id === step ? "text-[#1B1F5B]" : s.id < step ? "text-emerald-600" : "text-slate-400"} hidden sm:block`}>{s.label}</span>
              </button>
              {i < STEPS.length - 1 && <span className={`mx-2 h-px flex-1 ${s.id < step ? "bg-emerald-300" : "bg-slate-200"}`} />}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 py-6">
        {liveMode && step === 1 && (
          <div className="mb-4 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-bold text-emerald-600">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" /> Live fare, seat map & add-ons by TripJack
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-[1.6fr_1fr]">
          <form onSubmit={payNow} className="space-y-5">

            {/* ===== STEP 1: REVIEW ===== */}
            {step === 1 && (
              <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm" data-testid="step-review">
                <h3 className="text-sm font-extrabold uppercase tracking-wide text-slate-800">Review your itinerary</h3>
                <div className="mt-4 flex flex-col gap-3 rounded-xl bg-[#F5F7FB] p-4 sm:flex-row sm:items-center sm:gap-4">
                  <img src={airlineLogo(f.code, 90)} alt={f.airline} className="h-11 w-11 shrink-0 rounded-full border border-slate-100 bg-white object-contain p-1" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-bold text-slate-800">{f.airline} {f.flightNo}</p>
                    <p className="text-xs text-slate-400">{data.cabin} · {data.date}</p>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <div className="text-center"><p className="flex items-center gap-1 text-lg font-extrabold text-slate-800"><PlaneTakeoff className="h-4 w-4 text-slate-400" /> {f.depTime}</p><p className="text-xs text-slate-400">{data.fromCode}</p></div>
                    <span className="text-xs font-semibold text-slate-400">{f.duration}</span>
                    <div className="text-center"><p className="flex items-center gap-1 text-lg font-extrabold text-slate-800"><PlaneLanding className="h-4 w-4 text-slate-400" /> {f.arrTime}</p><p className="text-xs text-slate-400">{data.toCode}</p></div>
                  </div>
                </div>
                {segs.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {segs.map((s) => (
                      <div key={s.id} className="flex flex-col gap-1 rounded-xl border border-slate-100 px-4 py-2.5 text-sm sm:flex-row sm:items-center sm:justify-between sm:gap-2">
                        <span className="shrink-0 font-bold text-slate-700">{s.flightNo}</span>
                        <span className="min-w-0 text-slate-600">{s.from} ({s.fromCode}) {s.fromTerminal && `· ${s.fromTerminal}`} → {s.to} ({s.toCode}) {s.toTerminal && `· ${s.toTerminal}`}</span>
                        <span className="shrink-0 text-xs font-semibold text-slate-400">{Math.floor(s.duration / 60)}h {String(s.duration % 60).padStart(2, "0")}m</span>
                      </div>
                    ))}
                  </div>
                )}
                {reviewRet && (
                  <div data-testid="return-leg-block" className="mt-3">
                    <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-[#0E7C6B]"><Plane className="h-3.5 w-3.5" /> Return · {data?.retDate}</p>
                    <div className="space-y-2">
                      {(reviewRet.segments || []).map((s) => (
                        <div key={s.id} className="flex flex-col gap-1 rounded-xl border border-emerald-100 bg-emerald-50/40 px-4 py-2.5 text-sm sm:flex-row sm:items-center sm:justify-between sm:gap-2">
                          <span className="shrink-0 font-bold text-slate-700">{s.flightNo}</span>
                          <span className="min-w-0 text-slate-600">{s.from} ({s.fromCode}) {s.fromTerminal && `· ${s.fromTerminal}`} → {s.to} ({s.toCode}) {s.toTerminal && `· ${s.toTerminal}`}</span>
                          <span className="shrink-0 text-xs font-semibold text-slate-400">{Math.floor(s.duration / 60)}h {String(s.duration % 60).padStart(2, "0")}m</span>
                        </div>
                      ))}
                    </div>
                    <p className="mt-2 text-[11px] text-slate-400">Return leg seat selection auto-assigned; onward seats can be picked in step 3.</p>
                  </div>
                )}
                {f.refundable && (
                  <p className="mt-3 flex items-center gap-1.5 text-[12px] font-semibold text-emerald-600"><BadgeCheck className="h-4 w-4" /> Refundable fare</p>
                )}
                <button type="button" onClick={nextStep} data-testid="continue-step1"
                  className="mt-5 w-full rounded-xl bg-[#1B1F5B] py-3.5 text-sm font-extrabold tracking-wide text-white transition-colors hover:bg-[#141844]">
                  CONTINUE TO TRAVELLER DETAILS
                </button>
              </div>
            )}

            {/* ===== STEP 2: TRAVELLERS ===== */}
            {step === 2 && (
              <div className="space-y-5" data-testid="step-travellers">
                <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
                  <h3 className="flex items-center gap-2 text-sm font-extrabold uppercase tracking-wide text-slate-800"><User className="h-4 w-4 text-[#1B1F5B]" /> Traveller Details</h3>
                  {(cond.passportRequired || cond.passportExpiry || cond.passportIssue || cond.dobRequired || cond.panRequired) && (
                    <div data-testid="required-docs-banner" className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[12px] text-amber-800">
                      <p className="font-extrabold">This flight requires (per traveller):</p>
                      <ul className="mt-1 list-disc space-y-0.5 pl-4 font-medium">
                        {(cond.passportRequired || cond.passportExpiry || cond.passportIssue) && <li>Passport number{cond.passportIssue ? ", issue date" : ""}{cond.passportExpiry ? " & expiry date" : ""} (as printed on passport)</li>}
                        {cond.dobRequired && <li>Date of birth</li>}
                        {cond.panRequired && <li>PAN number</li>}
                      </ul>
                    </div>
                  )}
                  <div className="mt-4 space-y-4">
                    {travellers.map((t, i) => (
                      <div key={i} className="rounded-xl border border-slate-200 p-4" data-testid={`traveller-card-${i}`}>
                        <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400">Traveller {i + 1} · {t.pt}</p>
                        <div className="grid gap-2.5 sm:grid-cols-[90px_1fr_1fr]">
                          <select value={t.ti} onChange={(e) => setPax(i, { ti: e.target.value })} data-testid={`pax-title-${i}`} className="rounded-lg border border-slate-200 px-2 py-2.5 text-sm outline-none focus:border-[#1B1F5B]">
                            {TITLES.map((x) => <option key={x}>{x}</option>)}
                          </select>
                          <TravellerSuggest value={t.fN} type={t.pt} onChange={(v) => setPax(i, { fN: v })} onPick={(tr) => pickTraveller(i, tr)} testId={`pax-first-${i}`} className="rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#1B1F5B]" />
                          <input value={t.lN} onChange={(e) => setPax(i, { lN: e.target.value })} placeholder="Last name" data-testid={`pax-last-${i}`} className="rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#1B1F5B]" />
                        </div>
                        {(cond.dobRequired || cond.passportRequired || cond.passportExpiry || cond.passportIssue || t.pt !== "ADULT") && (
                          <div className="mt-2.5 grid gap-2.5 sm:grid-cols-3">
                            {(cond.dobRequired || t.pt !== "ADULT") && (
                              <label className="text-[11px] font-semibold text-slate-500">Date of birth
                                <input type="date" value={t.dob} onChange={(e) => setPax(i, { dob: e.target.value })} data-testid={`pax-dob-${i}`} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#1B1F5B]" />
                              </label>
                            )}
                            {(cond.passportRequired || cond.passportExpiry || cond.passportIssue) && (
                              <>
                                <label className="text-[11px] font-semibold text-slate-500">Passport no.
                                  <input value={t.pNum} onChange={(e) => setPax(i, { pNum: e.target.value })} placeholder="Passport number" data-testid={`pax-passport-${i}`} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#1B1F5B]" />
                                </label>
                                <label className="text-[11px] font-semibold text-slate-500">Passport issue date
                                  <input type="date" value={t.pid} onChange={(e) => setPax(i, { pid: e.target.value })} data-testid={`pax-pissue-${i}`} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#1B1F5B]" />
                                </label>
                                <label className="text-[11px] font-semibold text-slate-500">Passport expiry
                                  <input type="date" value={t.pep} onChange={(e) => setPax(i, { pep: e.target.value })} data-testid={`pax-pexp-${i}`} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#1B1F5B]" />
                                </label>
                              </>
                            )}
                            {cond.panRequired && (
                              <label className="text-[11px] font-semibold text-slate-500">PAN number
                                <input value={t.pan} onChange={(e) => setPax(i, { pan: e.target.value.toUpperCase() })} placeholder="ABCDE1234F" maxLength={10} data-testid={`pax-pan-${i}`} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm uppercase outline-none focus:border-[#1B1F5B]" />
                              </label>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
                  <h3 className="text-sm font-extrabold uppercase tracking-wide text-slate-800">Contact Details</h3>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <ContactDatalists /><input value={contact.phone} list={isAgent ? "saved-phones" : undefined} onChange={(e) => setContact({ ...contact, phone: e.target.value })} placeholder="Mobile number" data-testid="contact-phone" className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#1B1F5B]" />
                    <input type="email" value={contact.email} list={isAgent ? "saved-emails" : undefined} onChange={(e) => onContactEmail(e.target.value)} placeholder="Email (ticket will be sent here)" data-testid="contact-email" className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#1B1F5B]" />
                  </div>
                </div>

                <div className="flex gap-3">
                  <button type="button" onClick={() => setStep(1)} className="rounded-xl border border-slate-200 px-6 py-3 text-sm font-bold text-slate-600 hover:bg-slate-50">Back</button>
                  <button type="button" onClick={nextStep} data-testid="continue-step2"
                    className="flex-1 rounded-xl bg-[#1B1F5B] py-3 text-sm font-extrabold tracking-wide text-white transition-colors hover:bg-[#141844]">
                    CONTINUE TO SEATS & ADD-ONS
                  </button>
                </div>
              </div>
            )}

            {/* ===== STEP 3: SEATS & ADD-ONS ===== */}
            {step === 3 && (
              <div className="space-y-5" data-testid="step-addons">
                {/* Seat selection — map OR not-applicable notice */}
                {hasSeats ? (
                  <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
                    <h3 className="flex items-center gap-2 text-sm font-extrabold uppercase tracking-wide text-slate-800"><Armchair className="h-4 w-4 text-[#1B1F5B]" /> Seat Selection <span className="ml-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500">Optional</span></h3>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {travellers.map((t, i) => {
                        const seatLabels = Object.values(t.seat || {}).map((s) => s.seatNo).join(", ");
                        return (
                          <button key={i} type="button" onClick={() => setActivePax(i)} data-testid={`seat-pax-chip-${i}`}
                            className={`rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${activePax === i ? "border-[#1B1F5B] bg-[#1B1F5B] text-white" : "border-slate-200 text-slate-600 hover:border-[#1B1F5B]/50"}`}>
                            {t.fN || `Traveller ${i + 1}`}{seatLabels ? ` · ${seatLabels}` : ""}
                          </button>
                        );
                      })}
                    </div>
                    <p className="mt-2 text-[11px] text-slate-400">Pick a traveller above, then tap a seat. Tap again to remove.</p>
                    <div className="mt-3 space-y-4">
                      {seatMaps.map((sm) => {
                        const seg = segs.find((s) => s.id === sm.segId) || { from: data.fromCity, to: data.toCity, fromCode: data.fromCode, toCode: data.toCode, flightNo: f.flightNo };
                        return (
                          <SeatMap key={sm.segId} segment={seg} seats={sm.seats}
                            selectedSeatNo={travellers[activePax]?.seat?.[sm.segId]?.seatNo}
                            onSelect={(seat) => assignSeat(sm.segId, seat)} />
                        );
                      })}
                    </div>
                  </div>
                ) : seatsNA ? (
                  <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm" data-testid="seats-na">
                    <h3 className="flex items-center gap-2 text-sm font-extrabold uppercase tracking-wide text-slate-800"><Armchair className="h-4 w-4 text-[#1B1F5B]" /> Seat Selection</h3>
                    <div className="mt-3 flex items-start gap-3 rounded-xl bg-[#FFF9EC] px-4 py-3.5">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                      <p className="text-[12px] leading-relaxed text-[#8a6200]">
                        <span className="font-bold">Is fare ke liye advance seat selection available nahi hai.</span><br />
                        Aap web check-in (departure se 48 ghante pehle khulta hai) par free seat select kar sakte hain.
                      </p>
                    </div>
                  </div>
                ) : null}

                {hasAddons && (
                  <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
                    <h3 className="text-sm font-extrabold uppercase tracking-wide text-slate-800">Baggage & Meals <span className="ml-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500">Optional</span></h3>
                    <div className="mt-4 space-y-4">
                      {travellers.map((t, i) => (
                        <div key={i} className="rounded-xl border border-slate-200 p-4" data-testid={`addon-card-${i}`}>
                          <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400">{t.fN || `Traveller ${i + 1}`} · {t.pt}</p>
                          <div className="grid gap-2.5 sm:grid-cols-2">
                            {review.baggage.length > 0 && (
                              <label className="flex items-center gap-1 text-[11px] font-semibold text-slate-500"><Luggage className="h-3 w-3" /> Extra baggage
                                <select value={t.baggage} onChange={(e) => setPax(i, { baggage: e.target.value })} data-testid={`pax-baggage-${i}`} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#1B1F5B]">
                                  <option value="">No extra baggage</option>
                                  {review.baggage.map((b, bi) => <option key={`${b.code}-${b.segId}-${bi}`} value={b.code}>{b.desc} · {formatINR(b.amount)}</option>)}
                                </select>
                              </label>
                            )}
                            {review.meals.length > 0 && (
                              <label className="flex items-center gap-1 text-[11px] font-semibold text-slate-500"><Utensils className="h-3 w-3" /> Meal
                                <select value={t.meal} onChange={(e) => setPax(i, { meal: e.target.value })} data-testid={`pax-meal-${i}`} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#1B1F5B]">
                                  <option value="">No meal</option>
                                  {review.meals.map((m, mi) => <option key={`${m.code}-${m.segId}-${mi}`} value={m.code}>{m.desc} · {formatINR(m.amount)}</option>)}
                                </select>
                              </label>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {!hasSeats && !seatsNA && !hasAddons && (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center">
                    <Armchair className="mx-auto h-8 w-8 text-slate-300" />
                    <p className="mt-2 text-sm font-semibold text-slate-500">No seats or add-ons available for this fare</p>
                    <p className="mt-1 text-xs text-slate-400">Seats free select ho jayengi web check-in par. Aap payment continue kar sakte hain.</p>
                  </div>
                )}

                <div className="flex gap-3">
                  <button type="button" onClick={() => setStep(2)} className="rounded-xl border border-slate-200 px-6 py-3 text-sm font-bold text-slate-600 hover:bg-slate-50">Back</button>
                  <button type="button" onClick={nextStep} data-testid="continue-step3"
                    className="flex-1 rounded-xl bg-[#1B1F5B] py-3 text-sm font-extrabold tracking-wide text-white transition-colors hover:bg-[#141844]">
                    CONTINUE TO PAYMENT
                  </button>
                </div>
              </div>
            )}

            {/* ===== STEP 4: PAYMENT ===== */}
            {step === 4 && (
              <div className="space-y-5" data-testid="step-payment">
                <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
                  {isAgent ? (
                    <>
                      <h3 className="flex items-center gap-2 text-sm font-extrabold uppercase tracking-wide text-slate-800"><Wallet className="h-4 w-4 text-[#1B1F5B]" /> Pay from Agent Wallet</h3>
                      <div className="mt-3 flex items-center justify-between rounded-xl bg-[#F5F7FB] px-4 py-3.5"><span className="text-sm text-slate-600">Available balance</span><span className="text-lg font-extrabold text-[#1B1F5B]">{formatINR(walletBalance)}</span></div>
                      <div className="mt-2 flex items-center justify-between rounded-xl border border-dashed border-slate-200 px-4 py-3.5"><span className="text-sm text-slate-600">Payable amount</span><span className="text-lg font-extrabold text-slate-800">{formatINR(grandTotal)}</span></div>
                      <button type="submit" disabled={paying || !user} data-testid="pay-btn" className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#1B1F5B] py-3.5 text-sm font-extrabold tracking-wide text-white transition-colors hover:bg-[#141844] disabled:opacity-50">
                        {paying ? <><Loader2 className="h-4 w-4 animate-spin" /> Booking…</> : <><Wallet className="h-4 w-4" /> PAY {formatINR(grandTotal)} & BOOK</>}
                      </button>
                    </>
                  ) : (
                    <>
                      <h3 className="flex items-center gap-2 text-sm font-extrabold uppercase tracking-wide text-slate-800"><Lock className="h-4 w-4 text-[#1B1F5B]" /> Payment Method</h3>
                      <div className="mt-3 grid grid-cols-2 gap-2.5">
                        {PAY_METHODS.map((m) => (
                          <button type="button" key={m.id} onClick={() => setMethod(m.id)} data-testid={`pay-method-${m.id}`} className={`flex items-center gap-2.5 rounded-xl border px-3.5 py-3 text-left transition-all ${method === m.id ? "border-[#1B1F5B] bg-[#1B1F5B]/[0.04] shadow-sm" : "border-slate-200 hover:border-[#1B1F5B]/40"}`}>
                            <m.icon className={`h-5 w-5 ${method === m.id ? "text-[#1B1F5B]" : "text-slate-400"}`} />
                            <span><span className="block text-[13px] font-bold text-slate-800">{m.label}</span><span className="block text-[11px] text-slate-400">{m.sub}</span></span>
                          </button>
                        ))}
                      </div>
                      <button type="submit" disabled={paying || !!supplierBlock} data-testid="pay-btn" className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#F5B32B] py-3.5 text-sm font-extrabold tracking-wide text-[#0A0F2E] shadow-[0_8px_24px_rgba(245,179,43,0.4)] transition-all hover:bg-[#E89A10] disabled:opacity-50">
                        {paying ? <><Loader2 className="h-4 w-4 animate-spin" /> Opening Razorpay…</> : supplierBlock ? <><Lock className="h-4 w-4" /> BOOKING TEMPORARILY UNAVAILABLE</> : !user ? <><Lock className="h-4 w-4" /> CONTINUE & PAY {formatINR(grandTotal)}</> : <><Lock className="h-4 w-4" /> PAY {formatINR(grandTotal)} VIA RAZORPAY & BOOK</>}
                      </button>
                      <p className="mt-2.5 flex items-center justify-center gap-1.5 text-center text-[11px] text-slate-500 font-medium"><ShieldCheck className="h-3.5 w-3.5 text-emerald-500" /> 100% Secure Payment via Razorpay — UPI, Cards, NetBanking &amp; Wallets</p>
                      {!user && !otpFlow && (
                        <p className="mt-1 text-center text-[11px] text-slate-400">No login needed — we'll create your booking account with your email &amp; mobile automatically.</p>
                      )}
                      {otpFlow && (
                        <div data-testid="otp-box" className="mt-4 rounded-xl border border-[#1B1F5B]/20 bg-[#F3F6FF] p-4">
                          <p className="text-xs font-bold text-[#1B1F5B]">OTP sent to {otpFlow.email}</p>
                          <p className="mt-0.5 text-[11px] text-slate-500">Your past bookings live on this email. Enter OTP to continue securely.</p>
                          <div className="mt-2.5 flex gap-2">
                            <input data-testid="otp-input" value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                              placeholder="6-digit OTP" inputMode="numeric"
                              className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-center font-mono text-lg tracking-[0.3em] outline-none focus:border-[#1B1F5B]" />
                            <button data-testid="otp-verify-btn" onClick={verifyOtp} disabled={paying}
                              className="shrink-0 rounded-lg bg-[#1B1F5B] px-4 py-2.5 text-xs font-extrabold text-white disabled:opacity-50">
                              {paying ? "..." : "VERIFY"}
                            </button>
                          </div>
                        </div>
                      )}
                      {supplierBlock && (
                        <div data-testid="supplier-block-msg" className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3.5 text-center">
                          <p className="text-xs font-bold text-amber-700">⚠ {supplierBlock}</p>
                          <p className="mt-0.5 text-[11px] text-amber-600">No money will be charged. Please try booking after some time.</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
                <button type="button" onClick={() => setStep(3)} className="flex items-center gap-1.5 text-sm font-bold text-[#1B1F5B] hover:underline"><ArrowLeft className="h-4 w-4" /> Back to add-ons</button>
              </div>
            )}
          </form>

          {/* Fare summary - always visible */}
          <aside className="h-fit rounded-2xl border border-slate-100 bg-white p-5 shadow-sm lg:sticky lg:top-24" data-testid="fare-summary">
            <h3 className="text-sm font-extrabold uppercase tracking-wide text-slate-800">Fare Summary</h3>
            <div className="mt-3 space-y-2.5 text-sm">
              <div className="flex justify-between text-slate-600"><span>Base fare {liveMode ? "" : `(${data.pax}×)`}</span><span className="font-semibold text-slate-800">{formatINR(liveMode ? review.fare.base : Math.round(baseTotal / 1.18))}</span></div>
              <div className="flex justify-between text-slate-600"><span>Taxes &amp; fees</span><span className="font-semibold text-slate-800">{formatINR(liveMode ? review.fare.taxes : baseTotal - Math.round(baseTotal / 1.18))}</span></div>
              {addonTotal > 0 && <div className="flex justify-between text-slate-600"><span>Seats + add-ons</span><span className="font-semibold text-slate-800">{formatINR(addonTotal)}</span></div>}
              <div className="flex justify-between border-t border-dashed pt-3 text-base font-extrabold text-slate-800"><span>Total</span><span className="text-[#1B1F5B]">{formatINR(grandTotal)}</span></div>
            </div>
            {liveMode && (
              <div className="mt-4 flex gap-2 rounded-xl bg-[#F0FDF4] p-3 text-[11px] leading-relaxed text-emerald-700">
                <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>Fare re-validated live with TripJack. Seat map & add-on prices are real.</span>
              </div>
            )}
            <p className="mt-3 flex items-center gap-1.5 text-[11px] text-slate-400"><ShieldCheck className="h-3.5 w-3.5 text-emerald-500" /> 100% safe &amp; secure</p>
          </aside>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Checkout;
